import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Checkbox from '@repo/ui/checkbox'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { getStaffRequest } from '../../../../api/staff-requests/get-staff-request'
import { AffectedStaffStatusEnum } from '../../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { staffQualificationEnumMapping } from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import { AffectedStaffInput } from '../../../../lib/types/models/affectedStaff/affected-staff-input'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import { ItemNotFound } from '../../../elements/item-not-found'
import ValidationButtons from '../../../elements/validation-buttons'
import { affectedStaffSchema } from '../../staff-requests/utils'
import AffectedStaffPayscale from '../affected-staff-payscale'
import { AffectStaffRadFormFields } from '../affected-staff-rad-form-fields'

type Props = BaseFormProps<AffectedStaffInput, true> & {
  className?: string
  staffRequestId: number
  staffId: number
}

const AffectStaffForm = ({
  defaultValues,
  className,
  staffRequestId,
  staffId,
  ...validationButtonProps
}: Props) => {
  const { data, isPending } = useQuery({
    queryKey: ['staff-request', staffRequestId],
    queryFn: () => getStaffRequest(staffRequestId)
  })

  const form = useForm<AffectedStaffInput>({
    resolver: zodResolver(affectedStaffSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const qualificationType = data ? staffQualificationEnumMapping[data.qualification].type : null
  // English (foreign) staff cannot be assigned a trip
  const isForeignStaff = qualificationType === 'foreign'

  const centerTrips = useMemo(() => {
    const trips =
      data?.centers.flatMap((center) =>
        center.tripPlacesRequests.map((tripPlaceRequest) => tripPlaceRequest.trip)
      ) ?? []
    return Array.from(new Map(trips.map((trip) => [trip.id, trip])).values())
  }, [data?.centers])

  const tripOptions = useMemo(
    () =>
      centerTrips.map((trip) => ({
        label: TripProperties.getTripLabel(trip),
        value: trip.id
      })),
    [centerTrips]
  )

  useEffect(() => {
    if (isForeignStaff) {
      form.setValue('tripId', null, { shouldValidate: true })
      return
    }
    if (centerTrips.length !== 1) return
    form.setValue('tripId', centerTrips[0].id, { shouldValidate: true })
  }, [centerTrips, form, isForeignStaff])

  if (isPending) return <Loader center />
  if (!data) return <ItemNotFound title="Demande de staff non trouvée" />

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'divide-y divide-white-active space-y-4'}>
          <div className={cn('flex flex-col gap-3', className)}>
            <InnerBox className="w-fit py-2 rounded-sm">
              <FormField
                control={form.control}
                name={'status'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <Checkbox
                        {...field}
                        checked={field.value === AffectedStaffStatusEnum.PENDING}
                        label={'A confirmer'}
                        onChange={(checked) =>
                          field.onChange(
                            checked
                              ? AffectedStaffStatusEnum.PENDING
                              : AffectedStaffStatusEnum.AFFECTED
                          )
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </InnerBox>
            <div className="grid grid-cols-3 gap-3">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        required
                        mode="over"
                        label={'Date de début'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        required
                        mode="over"
                        label={'Date de fin'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tripId"
                render={({ field }) => (
                  <FormItem hidden={data.centers.length === 0 || isForeignStaff}>
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        label={'Voyage'}
                        options={tripOptions}
                        selectedOption={(() => {
                          return tripOptions.find((trip) => trip.value === field.value)
                        })()}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            {qualificationType === 'french' && <AffectStaffRadFormFields mode="create" />}
            <div className="flex gap-2 flex-col">
              <Typography variant="secondary-body" color={tailwindColors.primary.DEFAULT}>
                Rémunération
              </Typography>
              <AffectedStaffPayscale staffId={staffId} staffRequestId={staffRequestId} />
            </div>
          </div>
          <div className="pb-6 px-4">
            <ValidationButtons {...validationButtonProps} />
          </div>
        </div>
      </form>
    </Form>
  )
}

export default AffectStaffForm
